import DialogProvider from "@components/dialog_provider"
import { oneTimesUpdate } from "@domain/coupons"
import useTranslate from "@hooks/use_translate"
import { Alert, Stack, Typography } from "@mui/material"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import { useState } from "react"

type MigrateAiRolesProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  /** PROFILES moves AiProfile to one document per role; PROMPTS does the same for AiPrompts. */
  target: "PROFILES" | "PROMPTS"
}

const COPY = {
  PROFILES: {
    title: "Migrate AI Profiles to roles",
    type: OneTimesType.MigrateAiProfileRoles,
    body:
      "Drops the legacy unique index on AiProfile.customer, stamps every existing profile " +
      "with role SERVICE, and rebuilds the (customer, role) unique index.",
    success: "AI profiles migrated to roles."
  },
  PROMPTS: {
    title: "Migrate AI Prompts to roles",
    type: OneTimesType.MigrateAiPromptRoles,
    body:
      "Drops the legacy unique index on AiPrompts.name, stamps every existing prompt as the " +
      "shared row (role: null), clears the removed `env` field, and rebuilds the (name, role) " +
      "unique index.",
    success: "AI prompts migrated to roles."
  }
} as const

export default function MigrateAiRoles({
  isOpen,
  setIsOpen,
  target
}: MigrateAiRolesProps) {
  const { translate } = useTranslate()
  const [loading, setLoading] = useState(false)
  const copy = COPY[target]

  const handleClose = () => {
    if (loading) {
      return
    }
    setIsOpen(false)
  }

  async function onSubmit() {
    setLoading(true)
    const { isSuccess, errorMessage, data } = await oneTimesUpdate({
      type: copy.type,
      metaData: {}
    })
    setLoading(false)

    if (!isSuccess || !data) {
      showErrorToast(String(errorMessage))
      return
    }

    showSuccessToast(`${copy.success} ${JSON.stringify(data.data)}`)
    setIsOpen(false)
  }

  return (
    <DialogProvider
      title={copy.title}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("submit"),
        color: "primary",
        onClick: onSubmit,
        loading,
        disabled: loading
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <Typography variant="body2">{copy.body}</Typography>
        <Alert severity="info">
          Safe to re-run — the job is idempotent and never deletes a document.
        </Alert>
      </Stack>
    </DialogProvider>
  )
}
